/** In-document PDF text search. Hits use the same 0–100 page space as markup overlays. */

export interface PdfTextItemNorm {
  pageNumber: number;
  itemIndex: number;
  str: string;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface PdfTextSearchHit extends PdfTextItemNorm {
  text: string;
}

export interface PdfJsTextItemLike {
  str?: string;
  width?: number;
  height?: number;
  transform?: number[];
}

export function normalizePdfJsTextItem(
  item: PdfJsTextItemLike,
  pageNumber: number,
  itemIndex: number,
  pageWidth: number,
  pageHeight: number,
): PdfTextItemNorm | null {
  const str = typeof item.str === 'string' ? item.str : '';
  if (!str || pageWidth <= 0 || pageHeight <= 0) return null;
  const t = item.transform || [];
  const pdfX = Number(t[4]) || 0;
  const pdfY = Number(t[5]) || 0;
  const widthPt = Number(item.width) || Math.abs(Number(t[0]) || 0);
  const heightPt = Number(item.height) || Math.abs(Number(t[3]) || 0);
  return {
    pageNumber,
    itemIndex,
    str,
    x: (pdfX / pageWidth) * 100,
    y: (1 - (pdfY + heightPt) / pageHeight) * 100,
    width: (widthPt / pageWidth) * 100,
    height: (heightPt / pageHeight) * 100,
  };
}

export function findNormalizedTextHits(items: PdfTextItemNorm[], query: string): PdfTextSearchHit[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const hits: PdfTextSearchHit[] = [];
  for (const item of items) {
    if (!item.str.toLowerCase().includes(q)) continue;
    hits.push({ ...item, text: item.str });
  }
  return hits;
}

export async function collectNormalizedPdfText(pdf: {
  numPages: number;
  getPage: (n: number) => Promise<{
    getViewport: (opts: { scale: number }) => { width: number; height: number };
    getTextContent: () => Promise<{ items: PdfJsTextItemLike[] }>;
  }>;
}): Promise<PdfTextItemNorm[]> {
  const items: PdfTextItemNorm[] = [];
  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
    const page = await pdf.getPage(pageNumber);
    const viewport = page.getViewport({ scale: 1 });
    const content = await page.getTextContent();
    content.items.forEach((raw, itemIndex) => {
      const norm = normalizePdfJsTextItem(raw, pageNumber, itemIndex, viewport.width, viewport.height);
      if (norm) items.push(norm);
    });
  }
  return items;
}

export async function searchPdfDocument(
  pdf: {
    numPages: number;
    getPage: (n: number) => Promise<{
      getViewport: (opts: { scale: number }) => { width: number; height: number };
      getTextContent: () => Promise<{ items: PdfJsTextItemLike[] }>;
    }>;
  },
  query: string,
): Promise<PdfTextSearchHit[]> {
  if (!query.trim()) return [];
  const items = await collectNormalizedPdfText(pdf);
  return findNormalizedTextHits(items, query);
}
